import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Bell } from "lucide-react";
import Logo from "./Logo";
import BottomNav from "./BottomNav";
import { useAuth } from "@/context/AuthContext";

/**
 * PanelShell — desktop sidebar + mobile bottom nav for Client / Driver / Admin panels.
 */
export const PanelShell = ({
  role = "Cliente",
  navItems = [],
  bottomNavItems = [],
  userName = "Alejandra V.",
  userMeta = "Diamante",
  children,
  testId = "panel-shell",
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    // La cookie httpOnly no la puede borrar el JS del navegador -- hay que
    // esperar a que el backend la limpie con Set-Cookie antes de navegar.
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0D0E10] text-[#F5F5F5]" data-testid={testId}>
      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-[#2E2F33] bg-[#0D0E10] min-h-screen sticky top-0">
          <div className="px-6 py-6 border-b border-[#2E2F33]">
            <Logo size="md" to={null} />
          </div>

          <div className="px-4 py-6">
            <p className="px-3 text-[10px] uppercase tracking-brand text-[#A0A0A5] mb-3">Panel · {role}</p>
            <nav className="flex flex-col gap-1">
              {navItems.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-body transition-all ${
                        isActive
                          ? "bg-[#C9A227]/10 text-[#C9A227] border border-[#C9A227]/30"
                          : "text-[#A0A0A5] hover:text-[#F5F5F5] hover:bg-[#1C1D20] border border-transparent"
                      }`
                    }
                    data-testid={`sidebar-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon size={16} />
                    <span>{n.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </div>

          <div className="mt-auto px-4 py-6 border-t border-[#2E2F33]">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-9 h-9 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center text-[#C9A227] font-display font-bold text-sm">
                {userName.split(" ").map((s) => s[0]).join("").slice(0, 2)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-body font-medium truncate">{userName}</p>
                <p className="text-xs text-[#A0A0A5] truncate">{userMeta}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 text-xs uppercase tracking-wider text-[#A0A0A5] hover:text-[#C9A227] transition"
              data-testid="sidebar-logout"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          {/* Top bar mobile */}
          <div className="lg:hidden sticky top-0 z-20 bg-[#0D0E10]/95 backdrop-blur border-b border-[#2E2F33] px-4 py-3 flex items-center justify-between">
            <Logo size="sm" to={null} />
            <div className="flex items-center gap-3">
              <button className="text-[#A0A0A5]" data-testid="topbar-bell"><Bell size={18} /></button>
              <div className="w-8 h-8 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center text-[#C9A227] font-display font-bold text-xs">
                {userName.split(" ").map((s) => s[0]).join("").slice(0, 2)}
              </div>
            </div>
          </div>

          <div className="pb-24 lg:pb-8">{children}</div>
        </main>
      </div>

      {bottomNavItems.length > 0 && (
        <BottomNav items={bottomNavItems} testIdPrefix={`${role.toLowerCase()}-bottom-nav`} />
      )}
    </div>
  );
};

export default PanelShell;
